import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CircleNotch, Storefront, ArrowRight, Palette, MagnifyingGlass, X, Heart, Scales } from '@phosphor-icons/react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useCurrency } from '@/context/CurrencyContext';
import { trackWishlistAdd } from '@/lib/analytics';
import useSeoMeta from '@/lib/useSeoMeta';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { key: 'all', label: 'All' },
  { key: 'sports', label: 'Running & Casual' },
  { key: 'training', label: 'Training & Gym', isTag: true },
  { key: 'basketball', label: 'Basketball' },
  { key: 'football', label: 'Football / Rugby / Soccer' },
  { key: 'boots', label: 'Boots' },
  { key: 'outdoor', label: 'Outdoor & Hiking' },
  { key: 'womens_fashion', label: "Women's Fashion" },
  { key: 'mens_fashion', label: "Men's Fashion" },
  { key: 'sandals', label: 'Sandals & Flip Flops' },
  { key: 'school', label: 'School Shoes' },
  { key: 'kids', label: 'Kids' },
  { key: 'walking', label: 'Walking', isTag: true },
];
const brands = ['All', 'Adidas', 'Allen Edmonds', 'Aquila', 'ASICS', 'Birkenstock', 'Blundstone', 'Bobux', 'Brooks', 'Canterbury', 'Clarks', 'Cole Haan', 'Converse', 'Cosyfeet', 'Crocs', 'Dr. Martens', 'Durango', 'Esska', 'Florsheim', 'Georgia Boot', 'HOKA', 'Jordan', 'KEEN', 'Merrell', 'Mizuno', 'New Balance', 'Nike', 'On', 'Puma', 'Reebok', 'Rocky', 'Salomon', 'Saucony', 'Skechers', 'Steve Madden', 'Stride Rite', 'Timberland', 'Tony Bianco', 'UGG', 'Under Armour', 'Vans', 'Wittner'];
const colorOptions = [
  { value: '', label: 'All', hex: null },
  { value: 'black', label: 'Black', hex: '#0A0A0A' },
  { value: 'white', label: 'White', hex: '#FFFFFF' },
  { value: 'grey', label: 'Grey', hex: '#9CA3AF' },
  { value: 'brown', label: 'Brown', hex: '#92400E' },
  { value: 'blue', label: 'Blue', hex: '#3B82F6' },
  { value: 'red', label: 'Red', hex: '#EF4444' },
  { value: 'pink', label: 'Pink', hex: '#EC4899' },
  { value: 'green', label: 'Green', hex: '#22C55E' },
  { value: 'navy', label: 'Navy', hex: '#1E3A5F' },
  { value: 'nude', label: 'Nude', hex: '#D4A982' },
];
const countryFlags = { AU: '\uD83C\uDDE6\uD83C\uDDFA', UK: '\uD83C\uDDEC\uD83C\uDDE7', US: '\uD83C\uDDFA\uD83C\uDDF8' };

export default function BrowsePage() {
  const [searchParams] = useSearchParams();
  const [shoes, setShoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState(() => {
    const cat = searchParams.get('category');
    const tag = searchParams.get('tag');
    if (tag) {
      const match = categories.find(c => c.key === tag && c.isTag);
      return match || { key: tag, isTag: true, label: tag };
    }
    if (cat) {
      const match = categories.find(c => c.key === cat && !c.isTag);
      return match || { key: cat, label: cat };
    }
    return { key: 'all' };
  });
  const [brandFilter, setBrandFilter] = useState('All');
  const [colorFilter, setColorFilter] = useState('');
  const [viewMode, setViewMode] = useState('products');
  const [storeDir, setStoreDir] = useState(null);
  const [storeCountry, setStoreCountry] = useState('AU');
  const [storeCategory, setStoreCategory] = useState('all');
  const [wishlistIds, setWishlistIds] = useState(new Set());
  const [compareIds, setCompareIds] = useState([]);
  const { user } = useAuth();
  const { format, region } = useCurrency();
  const navigate = useNavigate();

  // Search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  // SEO: per-region hreflang tags + canonical for the Stores view.
  // Tells Google to show /browse?region=AU to Aussie searchers and /browse?region=UK
  // to Brits, rather than letting the algorithm guess from the catalog mix.
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://solefinder.app';
  const viewSuffix = viewMode === 'stores' ? `&view=stores` : '';
  useSeoMeta({
    title: viewMode === 'stores'
      ? `Shop ${storeCountry === 'AU' ? 'Australian' : storeCountry === 'UK' ? 'UK' : 'US'} Shoe Retailers · SoleFinder`
      : 'Browse Shoes · Compare Prices Across AU, UK & US · SoleFinder',
    description: viewMode === 'stores'
      ? `Discover trusted shoe retailers in ${storeCountry === 'AU' ? 'Australia' : storeCountry === 'UK' ? 'the United Kingdom' : 'the United States'}. Compare prices, find your fit, and shop from 40+ verified stores across multiple categories.`
      : 'Browse 1,400+ shoes from 40+ trusted retailers across Australia, the UK, and the US. Filter by brand, activity, and category. Side-by-side price comparison on every shoe.',
    canonical: viewMode === 'stores'
      ? `${baseUrl}/browse?region=${storeCountry}${viewSuffix}`
      : `${baseUrl}/browse`,
    hreflang: viewMode === 'stores' ? [
      { hreflang: 'en-AU', href: `${baseUrl}/browse?region=AU${viewSuffix}` },
      { hreflang: 'en-GB', href: `${baseUrl}/browse?region=UK${viewSuffix}` },
      { hreflang: 'en-US', href: `${baseUrl}/browse?region=US${viewSuffix}` },
      // x-default tells Google "if user isn't in AU/UK/US, show this one"
      { hreflang: 'x-default', href: `${baseUrl}/browse${viewSuffix}` },
    ] : undefined,
    hreflangKey: 'browse',
  });

  // Fetch wishlist IDs for logged-in user
  useEffect(() => {
    if (user) {
      axios.get(`${API}/wishlist`, { withCredentials: true })
        .then(({ data }) => setWishlistIds(new Set(data.map(s => s.shoe_id))))
        .catch(() => {});
    }
  }, [user]);

  const toggleWishlist = async (e, shoeId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { navigate('/login'); return; }
    try {
      if (wishlistIds.has(shoeId)) {
        await axios.delete(`${API}/wishlist/${shoeId}`, { withCredentials: true });
        setWishlistIds(prev => { const n = new Set(prev); n.delete(shoeId); return n; });
      } else {
        await axios.post(`${API}/wishlist`, { shoe_id: shoeId }, { withCredentials: true });
        setWishlistIds(prev => new Set(prev).add(shoeId));
        // GA: only ADDs, not removes
        const target = shoes.find(s => s.shoe_id === shoeId);
        trackWishlistAdd({ shoeId, brand: target?.brand });
      }
      window.dispatchEvent(new Event('wishlist-updated'));
    } catch (err) {
      if (err.response?.status === 401) navigate('/login');
    }
  };

  const toggleCompare = (e, shoeId) => {
    e.preventDefault();
    e.stopPropagation();
    if (compareIds.includes(shoeId)) {
      setCompareIds(compareIds.filter(id => id !== shoeId));
    } else if (compareIds.length < 4) {
      setCompareIds([...compareIds, shoeId]);
    }
  };

  const goCompare = () => {
    navigate('/compare', { state: { preSelected: compareIds } });
  };

  useEffect(() => {
    const params = {};
    if (activeFilter.key !== 'all') {
      if (activeFilter.isTag) {
        params.tag = activeFilter.key;
      } else {
        params.category = activeFilter.key;
      }
    }
    if (brandFilter !== 'All') params.brand = brandFilter;
    if (colorFilter) params.color = colorFilter;
    if (region) params.region = region;
    setLoading(true);
    axios.get(`${API}/shoes`, { params })
      .then(({ data }) => setShoes(data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [activeFilter, brandFilter, colorFilter, region]);

  useEffect(() => {
    if (viewMode === 'stores' && !storeDir) {
      axios.get(`${API}/store-directory`).then(({ data }) => setStoreDir(data)).catch(console.error);
    }
  }, [viewMode, storeDir]);

  // Debounced search
  const doSearch = useCallback((q) => {
    if (!q || q.length < 2) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    axios.get(`${API}/search`, { params: { q, region } })
      .then(({ data }) => setSearchResults(data))
      .catch(console.error)
      .finally(() => setSearching(false));
  }, [region]);

  const handleSearchInput = (e) => {
    const q = e.target.value;
    setSearchQuery(q);
    setSearchOpen(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(q), 300);
  };

  const handleSearchSelect = (shoe) => {
    setSearchOpen(false);
    setSearchQuery('');
    navigate(`/shoe/${shoe.shoe_id}`);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter' && searchQuery.length >= 2) {
      setSearchOpen(false);
      // Apply search as a text filter on the browse page
      setActiveFilter({ key: 'all' });
      setBrandFilter('All');
      setColorFilter('');
      setLoading(true);
      axios.get(`${API}/shoes`, { params: { q: searchQuery, region } })
        .then(({ data }) => setShoes(data))
        .catch(console.error)
        .finally(() => setLoading(false));
    }
    if (e.key === 'Escape') {
      setSearchOpen(false);
    }
  };

  // Close search dropdown on outside click
  useEffect(() => {
    const handle = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setSearchOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const getStoresForView = () => {
    if (!storeDir || !storeDir[storeCountry]) return [];
    if (storeCategory === 'all') {
      const all = {};
      Object.values(storeDir[storeCountry]).forEach(stores => {
        stores.forEach(s => { if (!all[s.store_id]) all[s.store_id] = {...s, cats: []}; });
      });
      Object.entries(storeDir[storeCountry]).forEach(([cat, stores]) => {
        stores.forEach(s => { if (all[s.store_id]) all[s.store_id].cats.push(cat); });
      });
      return Object.values(all).sort((a, b) => a.name.localeCompare(b.name));
    }
    return (storeDir[storeCountry][storeCategory] || []).sort((a, b) => a.name.localeCompare(b.name));
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchOpen(false);
    setSearchResults([]);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="browse-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-10">
        <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#52525B] mb-2">Explore</p>
            <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter uppercase" data-testid="browse-title">
              {viewMode === 'products' ? 'All Shoes' : 'Store Directory'}
            </h1>
          </div>
          <div className="flex border-2 border-[#E4E4E7]">
            <button onClick={() => setViewMode('products')}
              className={`px-5 py-2.5 text-xs font-bold uppercase tracking-wide transition-colors ${viewMode === 'products' ? 'bg-[#0A0A0A] text-white' : 'hover:bg-[#F4F4F5]'}`}
              data-testid="view-products-btn">Products</button>
            <button onClick={() => setViewMode('stores')}
              className={`px-5 py-2.5 text-xs font-bold uppercase tracking-wide transition-colors flex items-center gap-1.5 ${viewMode === 'stores' ? 'bg-[#0A0A0A] text-white' : 'hover:bg-[#F4F4F5]'}`}
              data-testid="view-stores-btn"><Storefront size={14} /> Stores</button>
          </div>
        </div>

        {viewMode === 'products' && (
          <>
            {/* Search Bar */}
            <div className="mb-6 relative" ref={searchRef} data-testid="search-container">
              <div className="relative">
                <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
                <input
                  type="text"
                  placeholder="Search shoes, brands, categories..."
                  value={searchQuery}
                  onChange={handleSearchInput}
                  onKeyDown={handleSearchKeyDown}
                  onFocus={() => searchQuery.length >= 2 && setSearchOpen(true)}
                  className="w-full border-2 border-[#E4E4E7] bg-[#FAFAFA] pl-11 pr-10 py-3.5 text-sm font-medium focus:border-[#0A0A0A] focus:bg-white outline-none transition-all"
                  data-testid="search-input"
                />
                {searchQuery && (
                  <button onClick={clearSearch} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-[#0A0A0A]" data-testid="search-clear">
                    <X size={16} />
                  </button>
                )}
              </div>

              {/* Search Dropdown */}
              <AnimatePresence>
                {searchOpen && searchQuery.length >= 2 && (
                  <motion.div
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    className="absolute top-full left-0 right-0 bg-white border-2 border-[#0A0A0A] shadow-xl z-50 max-h-[400px] overflow-y-auto"
                    data-testid="search-dropdown">
                    {searching && (
                      <div className="flex items-center justify-center py-6">
                        <CircleNotch size={24} className="animate-spin text-[#FF3B30]" />
                      </div>
                    )}
                    {!searching && searchResults.length === 0 && (
                      <div className="px-4 py-6 text-center text-sm text-[#A1A1AA]">
                        No results for "{searchQuery}"
                      </div>
                    )}
                    {!searching && searchResults.map((shoe) => (
                      <button
                        key={shoe.shoe_id}
                        onClick={() => handleSearchSelect(shoe)}
                        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#F4F4F5] transition-colors border-b border-[#F4F4F5] last:border-b-0"
                        data-testid={`search-result-${shoe.shoe_id}`}>
                        <img src={shoe.image_url} alt={shoe.model} className="w-12 h-12 object-cover flex-shrink-0 border border-[#E4E4E7]" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-bold uppercase tracking-wide text-[#52525B]">{shoe.brand}</p>
                          <p className="text-sm font-bold truncate">{shoe.model}</p>
                          <p className="text-xs text-[#A1A1AA] capitalize">{shoe.category.replace('_', ' ')} &middot; ${shoe.price_range}</p>
                        </div>
                        <ArrowRight size={14} className="text-[#A1A1AA] flex-shrink-0" />
                      </button>
                    ))}
                    {!searching && searchResults.length > 0 && (
                      <button
                        onClick={() => { setSearchOpen(false); handleSearchKeyDown({ key: 'Enter' }); }}
                        className="w-full px-4 py-3 text-center text-xs font-bold uppercase tracking-wide text-[#FF3B30] hover:bg-[#FEF2F2] transition-colors"
                        data-testid="search-view-all">
                        View all results for "{searchQuery}"
                      </button>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="mb-8 space-y-4">
              <div className="flex gap-2 flex-wrap" data-testid="category-filter">
                {categories.map(c => (
                  <button key={c.key} onClick={() => { setActiveFilter(c); setSearchQuery(''); }}
                    className={`px-4 py-2 text-xs font-bold uppercase tracking-wide border-2 transition-colors ${activeFilter.key === c.key ? 'border-[#0A0A0A] bg-[#0A0A0A] text-white' : 'border-[#E4E4E7] hover:border-[#0A0A0A]'}`}
                    data-testid={`cat-filter-${c.key}`}>
                    {c.label}
                  </button>
                ))}
              </div>
              <div className="flex gap-2 flex-wrap" data-testid="brand-filter">
                {brands.map(b => (
                  <button key={b} onClick={() => setBrandFilter(b)}
                    className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wide border transition-colors ${brandFilter === b ? 'border-[#FF3B30] bg-[#FF3B30] text-white' : 'border-[#E4E4E7] hover:border-[#FF3B30]'}`}
                    data-testid={`brand-filter-${b.toLowerCase()}`}>
                    {b}
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center py-20"><CircleNotch size={48} className="animate-spin text-[#FF3B30]" /></div>
            ) : (
              <>
                <p className="text-xs font-bold uppercase tracking-wide text-[#A1A1AA] mb-4" data-testid="results-count">{shoes.length} shoes found</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
                  {shoes.map((shoe, i) => {
                    const inWl = wishlistIds.has(shoe.shoe_id);
                    const inCmp = compareIds.includes(shoe.shoe_id);
                    return (
                    <motion.div key={shoe.shoe_id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.03, 0.5) }}>
                      <div className={`relative border bg-white transition-all group ${inCmp ? 'border-[#FF3B30] ring-1 ring-[#FF3B30]/20' : 'border-[#E4E4E7] hover:border-[#0A0A0A]'}`} data-testid={`browse-card-${shoe.shoe_id}`}>
                        {/* Action buttons */}
                        <div className="absolute top-2 right-2 z-10 flex flex-col gap-1.5">
                          <button onClick={(e) => toggleWishlist(e, shoe.shoe_id)}
                            className={`w-8 h-8 flex items-center justify-center transition-all ${inWl ? 'bg-[#FF3B30] text-white' : 'bg-white/80 text-[#52525B] hover:text-[#FF3B30]'}`}
                            data-testid={`browse-wishlist-${shoe.shoe_id}`}>
                            <Heart size={16} weight={inWl ? 'fill' : 'regular'} />
                          </button>
                          <button onClick={(e) => toggleCompare(e, shoe.shoe_id)}
                            className={`w-8 h-8 flex items-center justify-center transition-all ${inCmp ? 'bg-[#0A0A0A] text-white' : 'bg-white/80 text-[#52525B] hover:text-[#0A0A0A]'}`}
                            disabled={!inCmp && compareIds.length >= 4}
                            data-testid={`browse-compare-${shoe.shoe_id}`}>
                            <Scales size={16} />
                          </button>
                        </div>
                        <Link to={`/shoe/${shoe.shoe_id}`}>
                          <div className="overflow-hidden">
                            <img src={shoe.image_url} alt={shoe.model} className="w-full aspect-square object-cover group-hover:scale-105 transition-transform duration-300" />
                          </div>
                          <div className="p-4">
                            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#52525B]">{shoe.brand}</p>
                            <h3 className="font-heading font-bold text-sm mt-1">{shoe.model}</h3>
                            <p className="text-xs text-[#52525B] capitalize mt-1">{shoe.category.replace('_', ' ')} &middot; {shoe.fit_type} fit</p>
                            <p className="font-bold text-sm mt-2">{format(shoe.base_price || parseFloat(String(shoe.price_range||'0').split('-')[0]) || 0, 'USD')}</p>
                          </div>
                        </Link>
                      </div>
                    </motion.div>
                    );
                  })}
                </div>
              </>
            )}

            {!loading && shoes.length === 0 && (
              <div className="text-center py-20 border border-[#E4E4E7]">
                <p className="text-[#52525B] text-lg">No shoes found matching your filters.</p>
                {searchQuery && (
                  <button onClick={clearSearch} className="mt-3 text-sm font-bold text-[#FF3B30] uppercase">Clear Search</button>
                )}
              </div>
            )}
          </>
        )}

        {viewMode === 'stores' && (
          <div data-testid="store-directory">
            <div className="mb-6 flex gap-3" data-testid="store-country-filter">
              {['AU', 'UK', 'US'].map(c => (
                <button key={c} onClick={() => setStoreCountry(c)}
                  className={`px-6 py-3 text-sm font-bold uppercase tracking-wide border-2 transition-colors flex items-center gap-2 ${storeCountry === c ? 'border-[#0A0A0A] bg-[#0A0A0A] text-white' : 'border-[#E4E4E7] hover:border-[#0A0A0A]'}`}
                  data-testid={`store-country-${c}`}>
                  {countryFlags[c]} {c === 'AU' ? 'Australia' : c === 'UK' ? 'United Kingdom' : 'United States'}
                </button>
              ))}
            </div>
            <div className="mb-6 flex gap-2 flex-wrap" data-testid="store-cat-filter">
              {categories.filter(c => !c.isTag).map(c => (
                <button key={c.key} onClick={() => setStoreCategory(c.key)}
                  className={`px-4 py-2 text-xs font-bold uppercase tracking-wide border-2 transition-colors ${storeCategory === c.key ? 'border-[#FF3B30] bg-[#FF3B30] text-white' : 'border-[#E4E4E7] hover:border-[#FF3B30]'}`}
                  data-testid={`store-cat-${c.key}`}>
                  {c.label}
                </button>
              ))}
            </div>

            {!storeDir ? (
              <div className="flex justify-center py-20"><CircleNotch size={48} className="animate-spin text-[#FF3B30]" /></div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {getStoresForView().map((store, i) => (
                  <motion.div key={store.store_id} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                    <Link to={`/store/${store.store_id}`}
                      className="block border border-[#E4E4E7] p-5 hover:border-[#0A0A0A] transition-colors group" data-testid={`store-card-${store.store_id}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-heading font-bold text-base group-hover:text-[#FF3B30] transition-colors">{store.name}</h3>
                          <p className="text-xs text-[#52525B] mt-1">
                            {store.product_count > 0 ? `${store.product_count} products available` : 'Store listed'}
                          </p>
                          {store.cats && (
                            <div className="flex gap-1.5 mt-2 flex-wrap">
                              {store.cats.map(cat => {
                                const catObj = categories.find(c => c.key === cat);
                                return (
                                  <span key={cat} className="text-[10px] font-bold uppercase tracking-wide bg-[#F4F4F5] text-[#52525B] px-2 py-0.5 border border-[#E4E4E7]">
                                    {catObj ? catObj.label : cat}
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </div>
                        <ArrowRight size={18} className="text-[#A1A1AA] group-hover:text-[#FF3B30] transition-colors shrink-0 mt-1" />
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            )}

            {storeDir && getStoresForView().length === 0 && (
              <div className="text-center py-20 border border-[#E4E4E7]">
                <p className="text-[#52525B]">No stores found for this category in {storeCountry === 'AU' ? 'Australia' : storeCountry === 'UK' ? 'United Kingdom' : 'United States'}.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Sticky compare bar */}
      {compareIds.length >= 2 && (
        <motion.div
          initial={{ y: 100 }} animate={{ y: 0 }}
          className="fixed bottom-0 left-0 right-0 bg-[#0A0A0A] text-white py-4 px-6 z-50 flex items-center justify-between"
          data-testid="browse-compare-sticky">
          <div className="flex items-center gap-4">
            <Scales size={20} />
            <span className="text-sm font-bold">{compareIds.length} shoes selected</span>
            <div className="hidden sm:flex gap-2">
              {compareIds.map(id => {
                const shoe = shoes.find(s => s.shoe_id === id);
                return shoe ? (
                  <div key={id} className="flex items-center gap-1 bg-white/10 px-2 py-1 text-xs">
                    <img src={shoe.image_url} alt="" className="w-5 h-5 object-cover" />
                    <span>{shoe.brand}</span>
                    <button onClick={(e) => toggleCompare(e, id)} className="hover:text-[#FF3B30] ml-1">&times;</button>
                  </div>
                ) : null;
              })}
            </div>
          </div>
          <button onClick={goCompare}
            className="bg-[#FF3B30] text-white font-bold uppercase tracking-wide text-sm px-8 py-3 hover:bg-[#D62828] transition-colors flex items-center gap-2"
            data-testid="browse-compare-go">
            <Scales size={16} /> Compare Now
          </button>
        </motion.div>
      )}
    </div>
  );
}
